import UrlGeneration from '@lovata/url-generation';

/**
 * @author  Andrey Kharanenka, a.khoronenko@lovata.com, LOVATA Group
 */
export default class ShopaholicFilterPanel {
	/**
	 * Represents a class for managing a product list in a shopaholic application.
	 * @class
	 * @param {ShopaholicProductList} obProductListHelper - An instance of the ShopaholicProductList class.
	 */
	constructor(obProductListHelper = null) {
		// Initialize instance variables
		this.obProductListHelper  = obProductListHelper;
		this.sEventType           = 'change';
		this.sFiledName           = 'property';
		this.sFilterType          = 'data-filter-type';
		this.sPropertyIDAttribute = 'data-property-id';
		
		this.sDefaultWrapperClass = '_shopaholic-filter-wrapper';
		this.sWrapperSelector     = `.${this.sDefaultWrapperClass}`;
	}
	
	/**
	 * Initializes event handlers for the specified wrapper selector and event type.
	 */
	init() {
		// Find all elements matching the wrapper selector
		const elements = document.querySelectorAll(this.sWrapperSelector);
		
		// Add event listener to each element
		elements.forEach((element) => {
			element.addEventListener(this.sEventType, () => {
				// Initialize URL generation
				UrlGeneration.init();
				
				// Prepare request data
				this.prepareRequestData();
				
				// Remove 'page' parameter from URL
				UrlGeneration.remove('page');
				
				// Update URL
				UrlGeneration.update();
				
				// Check if product list helper exists
				if (!this.obProductListHelper) {
					return;
				}
				
				// Send request using product list helper
				this.obProductListHelper.send();
			});
		});
	}
	
	/**
	 * Prepares the request data based on the filter inputs.
	 */
	prepareRequestData() {
		// Get all filter wrapper elements
		const filterWrapperList = document.querySelectorAll(this.sWrapperSelector);
		
		// If there are no filter wrappers, return
		if (filterWrapperList.length === 0) {
			return;
		}
		
		// Loop through each filter wrapper
		filterWrapperList.forEach((filterWrapper) => {
			// Get filter type attribute
			const filterType = filterWrapper.getAttribute(this.sFilterType);
			
			// Get property ID attribute
			const propertyID = filterWrapper.getAttribute(this.sPropertyIDAttribute);
			
			// Build the field name string
			let fieldName = `${this.sFiledName}`;
			
			// Append property ID to field name if it exists
			if (propertyID) {
				fieldName += `[${propertyID}]`;
			}
			
			// Initialize input list and value list
			let inputList   = null;
			const valueList = [];
			
			// Determine input list based on filter type
			switch (filterType) {
				case 'between':
					inputList = filterWrapper.querySelectorAll('input');
					break;
				case 'checkbox':
				case 'switch':
					inputList = filterWrapper.querySelectorAll('input[type="checkbox"]:checked');
					break;
				case 'select':
				case 'select_between':
					inputList = filterWrapper.querySelectorAll('select');
					break;
				case 'radio':
					inputList = filterWrapper.querySelectorAll('input[type="radio"]:checked');
					break;
			}
			
			// If there are no input elements, remove field name from URL and return
			if (!inputList || inputList.length === 0) {
				UrlGeneration.remove(fieldName);
				return;
			}
			
			// Loop through each input element
			inputList.forEach((input) => {
				const {value} = input;
				
				// If value is empty, skip to next iteration
				if (!value) {
					return;
				}
				
				// Add value to value list
				valueList.push(value);
			});
			
			// If there are no values, remove field name from URL
			// Otherwise, set field name with value list in URL
			if (!valueList || valueList.length === 0) {
				UrlGeneration.remove(fieldName);
			} else {
				UrlGeneration.set(fieldName, valueList);
			}
		});
	}
	
	/**
	 * Redeclare default selector of filter input
	 * Default value is "_shopaholic-filter-wrapper"
	 *
	 * @param {string} sWrapperSelector
	 * @returns {ShopaholicFilterPanel}
	 */
	setWrapperSelector(sWrapperSelector) {
		this.sWrapperSelector = sWrapperSelector;
		
		return this;
	}
	
	/**
	 * Redeclare default event type
	 * Default value is "change"
	 *
	 * @param {string} sEventType
	 * @returns {ShopaholicFilterPanel}
	 */
	setEventType(sEventType) {
		this.sEventType = sEventType;
		
		return this;
	}
	
	/**
	 * Redeclare default URL filed name
	 * Default value is "property"
	 *
	 * @param {string} sFieldName
	 * @returns {ShopaholicFilterPanel}
	 */
	setFieldName(sFieldName) {
		this.sFiledName = sFieldName;
		
		return this;
	}
}
